import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import API_URL from '../../config';
import './UserGroups.css';

function UserGroups() {
  const { getAuthHeader, isAdmin } = useAuth();

  const [groups, setGroups] = useState([]);
  // Surfaced by the mailing-list toggle, which can be refused with a 403.
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);

  // Create form
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // Edit form
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  // Member search
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const searchTimeout = useRef(null);

  // Family dropdown
  const [families, setFamilies] = useState([]);

  const fetchGroups = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/user-groups`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setGroups(data.groups || []);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const fetchFamilies = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/user-groups/families`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setFamilies(data.families || []);
      }
    } catch (err) {
      console.error('Error fetching families:', err);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchGroups();
    fetchFamilies();
  }, [fetchGroups, fetchFamilies]);

  const fetchGroupDetail = async (groupId) => {
    try {
      const response = await fetch(`${API_URL}/api/user-groups/${groupId}`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setExpandedGroup(data.group);
      }
    } catch (err) {
      console.error('Error fetching group detail:', err);
    }
  };

  const handleToggleExpand = (groupId) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
      setExpandedGroup(null);
      setMemberSearch('');
      setMemberResults([]);
    } else {
      setExpandedGroupId(groupId);
      fetchGroupDetail(groupId);
      setMemberSearch('');
      setMemberResults([]);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;

    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/user-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ name: newGroupName.trim(), description: newGroupDesc.trim() || undefined })
      });
      if (response.ok) {
        setNewGroupName('');
        setNewGroupDesc('');
        fetchGroups();
      }
    } catch (err) {
      console.error('Error creating group:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Delete this group? This cannot be undone.')) return;

    try {
      const response = await fetch(`${API_URL}/api/user-groups/${groupId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (response.ok) {
        if (expandedGroupId === groupId) {
          setExpandedGroupId(null);
          setExpandedGroup(null);
        }
        fetchGroups();
      }
    } catch (err) {
      console.error('Error deleting group:', err);
    }
  };

  const handleStartEdit = (group) => {
    setEditingGroupId(group.id);
    setEditName(group.name);
    setEditDesc(group.description || '');
  };

  // A mailing list can be targeted by the weekly open-weekend email, so it is
  // admin-controlled and its membership is admin-only from then on.
  const handleToggleMailingList = async (group) => {
    setError(null);
    const turningOn = !group.is_mailing_list;
    if (turningOn && !window.confirm(
      `Mark "${group.name}" as a mailing list?\n\n` +
      'It can then be targeted by automated emails, and only admins will be able to change who is in it.'
    )) return;

    try {
      const res = await fetch(`${API_URL}/api/user-groups/${group.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({
          name: group.name,
          description: group.description || undefined,
          isMailingList: turningOn
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Could not update the group.');
        return;
      }
      fetchGroups();
    } catch {
      setError('Could not update the group.');
    }
  };

  const handleSaveEdit = async (groupId) => {
    if (!editName.trim()) return;

    try {
      const response = await fetch(`${API_URL}/api/user-groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ name: editName.trim(), description: editDesc.trim() || undefined })
      });
      if (response.ok) {
        setEditingGroupId(null);
        fetchGroups();
        if (expandedGroupId === groupId) fetchGroupDetail(groupId);
      }
    } catch (err) {
      console.error('Error updating group:', err);
    }
  };

  // Member search
  const handleMemberSearch = (value) => {
    setMemberSearch(value);
    clearTimeout(searchTimeout.current);

    if (value.trim().length < 2) {
      setMemberResults([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/api/users/search?q=${encodeURIComponent(value.trim())}`, {
          headers: getAuthHeader()
        });
        if (response.ok) {
          const data = await response.json();
          // Filter out users already in the group
          const existingIds = (expandedGroup?.members || []).map(m => m.id);
          setMemberResults(data.filter(u => !existingIds.includes(u.id)));
        }
      } catch (err) {
        console.error('Error searching users:', err);
      }
    }, 300);
  };

  const handleAddMember = async (userId) => {
    if (!expandedGroupId) return;

    try {
      const response = await fetch(`${API_URL}/api/user-groups/${expandedGroupId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ userId })
      });
      if (response.ok) {
        setMemberSearch('');
        setMemberResults([]);
        fetchGroupDetail(expandedGroupId);
        fetchGroups();
      }
    } catch (err) {
      console.error('Error adding member:', err);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!expandedGroupId) return;

    try {
      const response = await fetch(`${API_URL}/api/user-groups/${expandedGroupId}/members/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (response.ok) {
        fetchGroupDetail(expandedGroupId);
        fetchGroups();
      }
    } catch (err) {
      console.error('Error removing member:', err);
    }
  };

  const handleAddFamily = async (family) => {
    if (!expandedGroupId || !family) return;

    try {
      const response = await fetch(`${API_URL}/api/user-groups/${expandedGroupId}/add-family`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ family })
      });
      if (response.ok) {
        const data = await response.json();
        alert(`Added ${data.added} member(s)${data.skipped > 0 ? `, ${data.skipped} already in group` : ''}`);
        fetchGroupDetail(expandedGroupId);
        fetchGroups();
      }
    } catch (err) {
      console.error('Error adding family:', err);
    }
  };

  if (loading) {
    return <div className="user-groups"><div className="loading">Loading groups...</div></div>;
  }

  return (
    <div className="user-groups">
      <h2>My Groups</h2>

      {/* Create Group Form */}
      <div className="create-group-form">
        <h3>Create New Group</h3>
        <form onSubmit={handleCreateGroup}>
          <div className="form-row">
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Group name (e.g., Family Trip Crew)"
              required
            />
          </div>
          <div className="form-row">
            <input
              type="text"
              value={newGroupDesc}
              onChange={(e) => setNewGroupDesc(e.target.value)}
              placeholder="Description (optional)"
            />
            <button type="submit" className="btn btn-success" disabled={creating || !newGroupName.trim()}>
              {creating ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>

      {/* Groups List */}
      {groups.length === 0 ? (
        <div className="empty-groups">
          <p>No groups yet.</p>
          <p>Create a group to quickly add members to reservations.</p>
        </div>
      ) : (
        <div className="groups-list">
          {error && (
            <div className="groups-error" role="alert">{error}</div>
          )}
          {groups.map(group => (
            <div key={group.id} className="group-card">
              <div className="group-header" onClick={() => handleToggleExpand(group.id)}>
                <div className="group-title-section">
                  {editingGroupId === group.id ? (
                    <div className="edit-group-form" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Group name"
                      />
                      <input
                        type="text"
                        value={editDesc}
                        onChange={(e) => setEditDesc(e.target.value)}
                        placeholder="Description"
                      />
                      <button className="btn btn-success btn-sm" onClick={() => handleSaveEdit(group.id)}>Save</button>
                      <button className="btn btn-sm" onClick={() => setEditingGroupId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <>
                      <span className="group-name">{group.name}</span>
                      {group.is_mailing_list && (
                        <span className="mailing-list-badge" title="Automated emails can target this group">
                          mailing list
                        </span>
                      )}
                      {group.description && <span className="group-description">{group.description}</span>}
                    </>
                  )}
                </div>
                <div className="group-meta">
                  <span className="member-count">{group.member_count} member{parseInt(group.member_count) !== 1 ? 's' : ''}</span>
                  <div className="group-actions" onClick={(e) => e.stopPropagation()}>
                    {isAdmin() && (
                      <button
                        className="btn btn-sm"
                        onClick={() => handleToggleMailingList(group)}
                        title={group.is_mailing_list
                          ? 'Stop this group being used for automated email'
                          : 'Allow automated email to target this group'}
                      >
                        {group.is_mailing_list ? 'Unmark list' : 'Mark as list'}
                      </button>
                    )}
                    <button className="btn btn-sm" onClick={() => handleStartEdit(group)} title="Edit group">Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteGroup(group.id)} title="Delete group">Delete</button>
                  </div>
                </div>
              </div>

              {expandedGroupId === group.id && expandedGroup && (
                <div className="group-body">
                  {/* Members List */}
                  <div className="group-members-list">
                    {expandedGroup.members.length === 0 ? (
                      <p style={{ color: 'var(--color-text-gray)', padding: '0.5rem 0' }}>No members yet. Add members below.</p>
                    ) : (
                      expandedGroup.members.map(member => (
                        <div key={member.id} className="group-member-item">
                          <div className="group-member-info">
                            <span className="group-member-name">{member.name}</span>
                            <span className="group-member-email">{member.email}{member.family ? ` (${member.family})` : ''}</span>
                          </div>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleRemoveMember(member.id)}
                            title="Remove from group"
                          >
                            &times;
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Members */}
                  <div className="add-members-section">
                    <h4>Add Members</h4>
                    <div className="add-member-controls">
                      {/* Search Input */}
                      <div className="member-search-wrap">
                        <input
                          type="text"
                          value={memberSearch}
                          onChange={(e) => handleMemberSearch(e.target.value)}
                          placeholder="Search users by name or email..."
                        />
                        {memberResults.length > 0 && (
                          <div className="member-results-dropdown">
                            {memberResults.map(u => (
                              <div
                                key={u.id}
                                className="member-result-item"
                                onClick={() => handleAddMember(u.id)}
                              >
                                <span className="result-name">{u.name}</span>
                                <span className="result-email">{u.email}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Add Family Dropdown */}
                      {families.length > 0 && (
                        <div className="family-add-row">
                          <select
                            defaultValue=""
                            onChange={(e) => {
                              if (e.target.value) {
                                handleAddFamily(e.target.value);
                                e.target.value = '';
                              }
                            }}
                          >
                            <option value="">Add entire family...</option>
                            {families.map(f => (
                              <option key={f} value={f}>{f}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default UserGroups;
